import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import './App.css';
import './i18n'; // Initialize i18n
import PreferencesForm from './components/PreferencesForm';
import StudyGuide from './components/StudyGuide';
import ExercisesPage from './components/ExercisesPage';
import PracticePage from './components/PracticePage';
import CEPTHomePage from './components/CEPTHomePage';
import SubjectLandingPage from './components/SubjectLandingPage';
import LanguageSwitcher from './components/LanguageSwitcher';
import ErrorModal from './components/ErrorModal';
import GrammarExercisesPage from './components/GrammarExercisesPage';
import logo from './logo.svg'; // Placeholder for logo
import { useTranslation } from 'react-i18next'; // Add translation hook

function App() {
  const [studyGuide, setStudyGuide] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [theme, setTheme] = useState('light');
  const [progress, setProgress] = useState(0);
  const [hideNavbar, setHideNavbar] = useState(false);
  const lastScrollY = useRef(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const [logoAnim, setLogoAnim] = useState(false);
  const [showAvatarPopup, setShowAvatarPopup] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [isFirstTimeVisitor, setIsFirstTimeVisitor] = useState(true);
  const { i18n } = useTranslation();

  // Auto-detect language on app load
  useEffect(() => {
    const detectAndSetLanguage = () => {
      // Get browser language
      const browserLang = navigator.language || navigator.userLanguage;
      console.log('🔍 Browser language detected:', browserLang);
      console.log('🔍 Current i18n language:', i18n.language);
      
      // Check if language is Chinese (various codes)
      if (browserLang && (browserLang.startsWith('zh') || browserLang.startsWith('cmn') || browserLang.startsWith('yue'))) {
        console.log('🇭🇰 Chinese language detected, switching to zh-TW');
        // Only change if not already set to Chinese
        if (i18n.language !== 'zh-TW') {
          i18n.changeLanguage('zh-TW').then(() => {
            console.log('✅ Language changed to zh-TW');
            // Force a re-render by updating a state
            setTheme(prevTheme => prevTheme); // This will trigger a re-render
          });
        } else {
          console.log('✅ Already in Chinese (zh-TW)');
        }
      } else {
        console.log('🇺🇸 Non-Chinese language detected, keeping English');
      }
    };

    // Run language detection after i18n is ready
    if (i18n.isInitialized) {
      console.log('🚀 i18n already initialized, running detection');
      detectAndSetLanguage();
    } else {
      console.log('⏳ Waiting for i18n to initialize...');
      i18n.on('initialized', () => {
        console.log('🚀 i18n initialized, running detection');
        detectAndSetLanguage();
      });
    }

    // Cleanup
    return () => {
      i18n.off('initialized', detectAndSetLanguage);
    };
  }, [i18n]);

  // Check if this is the first time visitor
  useEffect(() => {
    const hasVisitedBefore = localStorage.getItem('hasVisitedBefore');
    if (hasVisitedBefore) {
      setIsFirstTimeVisitor(false);
    } else {
      // Show help modal for first-time visitors
      setShowHelpModal(true);
      localStorage.setItem('hasVisitedBefore', 'true');
    }
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  useEffect(() => {
    const handleScroll = () => {
      if (window.innerWidth > 600) return; // Only apply on mobile
      const currentScrollY = window.scrollY;
      if (currentScrollY > lastScrollY.current && currentScrollY > 40) {
        setHideNavbar(true); // Scrolling down
      } else {
        setHideNavbar(false); // Scrolling up
      }
      lastScrollY.current = currentScrollY;
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (!hideNavbar) {
      setLogoAnim(true);
      const timeout = setTimeout(() => setLogoAnim(false), 800); // match animation duration
      return () => clearTimeout(timeout);
    }
  }, [hideNavbar]);

  const toggleTheme = () => {
    setTheme(prevTheme => prevTheme === 'light' ? 'dark' : 'light');
  };

  const handlePreferencesSubmit = async (preferences) => {
    setLoading(true);
    setError(null);
    setProgress(0);

    const attempt = async () => {
      const response = await fetch('/api/generate-study-guide', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          preferredQuestionTypes: preferences.preferredQuestionTypes || [],
          focusAreas: preferences.focusAreas || [],
          topic: preferences.topic || '',
          promptType: preferences.promptType || 'custom_english_mock',
          subject: 'english',
          grade: '10'
        })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) {
        const status = response.status;
        const msg = status === 429
          ? 'We are currently experiencing high demand. Retrying...'
          : 'Service temporary unavailable. Retrying...';
        throw new Error(msg);
      }
      if (!data.studyGuide || typeof data.studyGuide !== 'string' || !data.studyGuide.trim()) {
        throw new Error('No content returned. Retrying...');
      }
      return data;
    };

    try {
      // First attempt
      let data;
      try {
        data = await attempt();
      } catch (firstErr) {
        // Backoff then second attempt
        await new Promise(res => setTimeout(res, 1200));
        data = await attempt();
      }

      // Simulate progress
      const interval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 100) {
            clearInterval(interval);
            return 100;
          }
          return prev + 10;
        });
      }, 200);

      setStudyGuide(data.studyGuide);
    } catch (err) {
      console.error('Error generating study guide:', err);
      setError('We could not complete your request. Please try again in a moment.');
    } finally {
      setLoading(false);
    }
  };

  const isYue = (i18n.language || '').toLowerCase().startsWith('zh');
  const txt = {
    title: isYue ? '📋 如何開始' : '📋 How to Get Started',
    step1: isYue ? '揀以下其中一項：' : 'Choose ONE of these options:',
    opt1: isYue ? '使用今日主題（建議）' : "Use Today's Topic (recommended)",
    opt2: isYue ? '由主題清單揀選' : 'Select from Topic List',
    opt3: isYue ? '輸入自訂主題' : 'Enter Custom Topic',
    step2: isYue ? '喺頁面最底按「GENERATE!／生成！」按鈕' : 'Click the GENERATE! button at the bottom',
    tip: isYue ? '貼士：你可以隨時喺導航列撳「幫助」再打開呢個教學。' : 'Tip: You can always access this help by clicking the "Help" button in the navigation bar.'
  };

  return (
    <Router>
      <div className="App">
        {/* Skip Link for Accessibility */}
        <a href="#main-content" className="skip-link">Skip to main content</a>
        
        <Navigation 
          theme={theme} 
          toggleTheme={toggleTheme} 
          menuOpen={menuOpen} 
          setMenuOpen={setMenuOpen}
          hideNavbar={hideNavbar}
          logoAnim={logoAnim}
          showHelpModal={showHelpModal}
          setShowHelpModal={setShowHelpModal}
        />
        
        <Routes>
          <Route path="/" element={
            <HomePage 
              studyGuide={studyGuide}
              loading={loading}
              error={error}
              progress={progress}
              onPreferencesSubmit={handlePreferencesSubmit}
              onResetStudyGuide={() => setStudyGuide(null)}
              onClearError={() => setError(null)}
            />
          } />
          <Route path="/hkdse/english" element={
            <HomePage 
              studyGuide={studyGuide}
              loading={loading}
              error={error}
              progress={progress}
              onPreferencesSubmit={handlePreferencesSubmit}
              onResetStudyGuide={() => setStudyGuide(null)}
              onClearError={() => setError(null)}
            />
          } />
          <Route path="/cambridge/cept" element={<CEPTHomePage />} />
          <Route path="/grammar" element={<GrammarExercisesPage />} />
          <Route path="/teachers" element={<TeachersPage />} />
        </Routes>
        
        <footer className="footer">
          <div className="footer-content">
            <span>© {new Date().getFullYear()} PaperTips. All rights reserved.</span>
            <div className="footer-contact">
              <span>Contact: <a href="mailto:papertips.space@gmail.com">papertips.space@gmail.com</a></span>
            </div>
          </div>
        </footer>
        
        {/* Help Modal */}
        {showHelpModal && (
          <div className="help-modal-overlay" onClick={() => setShowHelpModal(false)}>
            <div className="help-modal" onClick={(e) => e.stopPropagation()}>
              <button className="help-modal-close" onClick={() => setShowHelpModal(false)}>×</button>
              <div className="help-modal-content">
                <h2>{txt.title}</h2>
                <div className="help-steps">
                  <div className="help-step">
                    <span className="help-step-number">1</span>
                    <span className="help-step-text">{txt.step1}</span>
                  </div>
                  <div className="help-options">
                    <button 
                      className="help-option"
                      onClick={() => {
                        setShowHelpModal(false);
                        document.querySelector('.topic-of-day-card')?.scrollIntoView({ behavior: 'smooth' });
                      }}
                    >
                      <span className="help-option-icon">🎯</span>
                      <span className="help-option-text">{txt.opt1}</span>
                    </button>
                    <button 
                      className="help-option"
                      onClick={() => {
                        setShowHelpModal(false);
                        document.querySelector('.topics-menu-header')?.scrollIntoView({ behavior: 'smooth' });
                      }}
                    >
                      <span className="help-option-icon">✨</span>
                      <span className="help-option-text">{txt.opt2}</span>
                    </button>
                    <button 
                      className="help-option"
                      onClick={() => {
                        setShowHelpModal(false);
                        document.querySelector('.custom-topic-section')?.scrollIntoView({ behavior: 'smooth' });
                        document.getElementById('custom-topic-input')?.focus({ preventScroll: true });
                      }}
                    >
                      <span className="help-option-icon">✏️</span>
                      <span className="help-option-text">{txt.opt3}</span>
                    </button>
                  </div>
                  <div className="help-step">
                    <span className="help-step-number">2</span>
                    <span className="help-step-text">{txt.step2}</span>
                  </div>
                </div>
                <div className="help-tip">
                  <p><strong>💡 {isYue ? '貼士' : 'Tip'}:</strong> {txt.tip}</p>
                </div>
              </div>
            </div>
          </div>
        )}
        
        <div className="ai-disclaimer">
          <p>This content was generated using AI. While we strive for accuracy, we encourage readers to verify important information. We use AI-generated content to increase efficiencies and provide certain insights, but it may not reflect human expertise or opinions. Do not engage in dangerous or illegal activities.</p>
        </div>
      </div>
      <ErrorModal
        visible={!!error}
        title="Sorry, request failed"
        message={error || "Sorry, we're unable to complete your request right now. Please try again in a moment."}
        primaryLabel="Try again"
        onPrimary={() => window.location.reload()}
        onClose={() => setError(null)}
        secondaryLabel="Join Discord"
        onSecondary={() => window.open('https://discord.gg/cDg8e9aEvM', '_blank')}
      />
    </Router>
  );
}

// Navigation Component
function Navigation({ theme, toggleTheme, menuOpen, setMenuOpen, hideNavbar, logoAnim, showHelpModal, setShowHelpModal }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation(); // Add translation hook
  const lastScrollY = useRef(0);
  const [localHideNavbar, setLocalHideNavbar] = useState(false);
  const [showAvatarPopup, setShowAvatarPopup] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (window.innerWidth > 600) return; // Only apply on mobile
      const currentScrollY = window.scrollY;
      if (currentScrollY > lastScrollY.current && currentScrollY > 40) {
        setLocalHideNavbar(true); // Scrolling down
      } else {
        setLocalHideNavbar(false); // Scrolling up
      }
      lastScrollY.current = currentScrollY;
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (!localHideNavbar) {
      // Trigger logo animation when navbar becomes visible
      const timeout = setTimeout(() => {}, 800);
      return () => clearTimeout(timeout);
    }
  }, [localHideNavbar]);

  const handleNavigation = (path) => {
    setMenuOpen(false);
    if (path === '/') {
      navigate('/');
    } else if (path === '/cambridge/cept') {
      navigate('/cambridge/cept');
    } else if (path === '/teachers') {
      navigate('/teachers');
    } else if (path === '/hkdse/english') {
      // HKDSE English should navigate to home since that's where HKDSE content is
      navigate('/');
    } else if (path === '/grammar') {
      navigate('/grammar');
    } else {
      // Handle any other paths by navigating directly
      navigate(path);
    }
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  const handleAvatarClick = () => {
    setShowAvatarPopup(true);
  };

  return (
    <nav className={`navbar${localHideNavbar ? ' hide-navbar' : ''}`}>
      <div className="navbar-content">
        <div className="navbar-left">
          <div className="navbar-logo">
            <img src={logo} alt="PaperTips Logo" height={40} className={logoAnim ? 'logo-animate' : ''} />
            <span className="brand">PaperTips</span>
          </div>
        </div>
        <div className="navbar-mobile-right">
          <button 
            className="help-button" 
            onClick={() => setShowHelpModal(true)}
            aria-label="Help & Instructions"
            title="Help & Instructions"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="20" height="20">
              <circle cx="12" cy="12" r="10"/>
              <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
              <line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
            <span className="help-text">Help</span>
          </button>
          
          <button 
            className="navbar-discord-btn" 
            onClick={() => window.open('https://discord.gg/cDg8e9aEvM', '_blank')}
            aria-label="Join Discord Community"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
              <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 1-.079-.037A19.736 19.791 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057a19.9 19.791 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028a14.09 14.107 0 0 0 1.226-1.994a.076.076 0 0 0-.041-.106a13.107 13.107 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128a10.2 10.2 0 0 0 .372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127a12.299 12.299 0 0 1-1.873.892a.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.839 19.791 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.956-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.955-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.946 2.418-2.157 2.418z"/>
            </svg>
          </button>
          
          <button className="navbar-hamburger" onClick={() => setMenuOpen(m => !m)} aria-label={menuOpen ? "Close menu" : "Open menu"}>
            <span className="hamburger-icon">{menuOpen ? '×' : '☰'}</span>
          </button>
        </div>
      </div>
      <ul className="navbar-links">
        {/* Navigation links removed - only hamburger menu will be used */}
      </ul>
      {menuOpen && (
        <div className="navbar-dropdown">
          <ul>
            <li className="dropdown-header">☰ Menu</li>
            <li className="quick-icons-container">
              <div className="quick-icons">
                <button className="icon-only" onClick={toggleTheme} aria-label="Toggle theme">
                  {theme === 'light' ? (
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 12.79A9 9 0 1 1 11.21 3a7 7 0 0 0 9.79 9.79z"/>
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="5"/>
                      <line x1="12" y1="1" x2="12" y2="3"/>
                      <line x1="12" y1="21" x2="12" y2="23"/>
                      <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
                      <line x1="18.36" y1="18.36" x2="19.78" y2="18.36"/>
                      <line x1="1" y1="12" x2="3" y2="12"/>
                      <line x1="21" y1="12" x2="23" y2="12"/>
                      <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
                      <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
                    </svg>
                  )}
                </button>
                <LanguageSwitcher iconOnly />
                <button className="icon-only" onClick={handleAvatarClick} aria-label="User profile">
                  <img src="https://api.dicebear.com/7.x/bottts/svg?seed=student" alt="User Avatar" width="24" height="24" style={{ borderRadius: '50%', border: '2px solid var(--accent, #ffd600)' }} />
                </button>
              </div>
            </li>
            <li className="dropdown-subheader">📚 Subjects</li>
            <li onClick={() => handleNavigation('/hkdse/english')}>🇭🇰 HKDSE English</li>
            <li onClick={() => handleNavigation('/cambridge/cept')}>🇬🇧 Cambridge CEPT</li>
            <li onClick={() => handleNavigation('/grammar')}>📝 Grammar Practice</li>
            <li className="dropdown-subheader">👨‍🏫 Teachers</li>
            <li onClick={() => handleNavigation('/teachers')}>📋 For Teachers</li>
          </ul>
        </div>
      )}
      
      {/* Avatar Popup */}
      {showAvatarPopup && (
        <div className="avatar-popup-overlay" onClick={() => setShowAvatarPopup(false)}>
          <div className="avatar-popup" onClick={(e) => e.stopPropagation()}>
            <button className="avatar-popup-close" onClick={() => setShowAvatarPopup(false)}>×</button>
            <div className="avatar-popup-content">
              <h3>�� Profile & Avatar</h3>
              <p>Profile editing and avatar customization features are coming soon!</p>
              <p>We're working hard to bring you personalized profile options in future updates.</p>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}

// HomePage Component
function HomePage({ studyGuide, loading, error, progress, onPreferencesSubmit, onResetStudyGuide, onClearError }) {
  const studyGuideRef = useRef(null);
  const { t, i18n } = useTranslation(); // Add translation hook

  // Debug logging for language detection
  useEffect(() => {
    console.log('🏠 HomePage - Current language:', i18n.language);
    console.log('🏠 HomePage - Translation function working:', t('hero.title'));
  }, [i18n.language, t]);

  // Auto-scroll to HKDSE block when study guide is generated
  useEffect(() => {
    if (studyGuide && studyGuideRef.current) {
      // Add a small delay to ensure the content is rendered
      setTimeout(() => {
        studyGuideRef.current.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start' 
        });
      }, 100);
    }
  }, [studyGuide]);

  return (
    <main role="main" aria-label="PaperTips Main Content" id="main-content">
      <div className="page-content-wrapper">
        <header className="hero" role="banner">
          <div className="hero-content">
            <h1>{t('hero.title', 'Smarter Study, Better Results!')}</h1>
            <p>{t('hero.subtitle', 'AI-powered HKDSE exercises and mock exam papers with AI marking and personalized feedback for students and teachers.')}</p>
            
            {/* Chinese content visible to Googlebot */}
            <div className="hero-chinese" style={{ display: 'none' }} aria-hidden="true">
              <h1>更聰明的學習，更好的成績！</h1>
              <p>AI驅動的HKDSE練習題和模擬試卷，配備AI評分和個性化反饋，為學生和教師提供支持。</p>
            </div>
            
            <div className="hero-buttons">
              <button className="cta-btn" onClick={() => window.scrollTo({top: 600, behavior: 'smooth'})}>{t('hero.getStarted', 'Get Started')}</button>
              <button 
                className="discord-hero-btn" 
                onClick={() => window.open('https://discord.gg/cDg8e9aEvM', '_blank')}
                aria-label="Join Discord Community"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                  <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 1-.079-.037A19.736 19.791 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057a19.9 19.791 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028a14.09 14.107 0 0 0 1.226-1.994a.076.076 0 0 0-.041-.106a13.107 13.107 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128a10.2 10.2 0 0 0 .372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127a12.299 12.299 0 0 1-1.873.892a.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.839 19.791 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.956-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.955-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.946 2.418-2.157 2.418z"/>
                </svg>
                <span className="discord-text">{t('hero.joinDiscord', 'Join Our Online Discord Community')}</span>
              </button>
            </div>
          </div>
        </header>
        
        <section className="main-content" role="region" aria-label="Main Content">
          
          
          {loading && (
            <section className="loading-container" aria-live="polite" aria-label="Loading content">
              <div className="loading">{t('loading.generatingHKDSE', 'Generating your exercises and mock exam paper...')}</div>
              <div className="loading-skeleton">
                <div className="skel-line long"></div>
                <div className="skel-line medium"></div>
                <div className="skel-line long"></div>
                <div className="skel-line short"></div>
              </div>
              <div className="progress-shimmer"></div>
              
              {/* Chinese loading content for Googlebot */}
              <div className="loading-chinese" style={{ display: 'none' }} aria-hidden="true">
                <div className="loading">正在生成你的練習題和模擬試卷...</div>
              </div>
            </section>
          )}
          
          {/* Suppress inline error; use modal instead */}
          {error && null}
          
          {!studyGuide && !loading && (
            <section className="preferences-section" aria-label="Practice preferences form">
              <PreferencesForm onSubmit={onPreferencesSubmit} />
            </section>
          )}
          
          {studyGuide && (
            <article className="study-guide" ref={studyGuideRef} aria-label="Generated study guide">
              <header>
                <h2>{t('hkdse.generatePaper', 'HKDSE English Mock Exam Paper')}</h2>
                
                {/* Chinese content for Googlebot */}
                <div className="chinese-content" aria-hidden="true">
                  <h2>HKDSE英文模擬試卷</h2>
                </div>
              </header>
              {console.log('Study Guide Content:', studyGuide)}
              <StudyGuide content={studyGuide} />
              <footer>
                <button 
                  className="cta-btn"
                  onClick={() => {
                    onResetStudyGuide();
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  style={{ marginTop: '2rem' }}
                >
                  {t('buttons.generateNew', 'Generate New Set')}
                </button>
                
                {/* Chinese button text for Googlebot */}
                <div className="chinese-content" aria-hidden="true">
                  <button>生成新套題</button>
                </div>
              </footer>
            </article>
          )}
          
          {/* Additional SEO content for Googlebot */}
          <section className="seo-content" aria-hidden="true">
            <h2>PaperTips - 香港英文考試練習平台</h2>
            <p>PaperTips是一個專為香港學生設計的AI驅動英文學習平台。我們提供免費的HKDSE和CEPT英文練習試題，配備即時AI評分系統和個性化反饋。</p>
            
            <h3>主要功能</h3>
            <ul>
              <li>HKDSE英文閱讀練習</li>
              <li>CEPT英文考試準備</li>
              <li>AI即時評分系統</li>
              <li>個性化學習建議</li>
              <li>免費使用，無需註冊</li>
            </ul>
            
            <h3>適合用戶</h3>
            <p>本平台特別適合準備HKDSE英文考試的香港中學生，以及需要提升英文水平的CEPT考生。我們的AI系統能夠即時分析答案，提供詳細的評分和改進建議。</p>
            
            <h3>技術特色</h3>
            <p>PaperTips採用先進的人工智能技術，能夠理解學生的答案內容，提供準確的評分和有用的學習建議。我們的系統持續學習和改進，確保評分質量的不斷提升。</p>
          </section>
        </section>
      </div>
    </main>
  );
}

// TeachersPage Component (placeholder)
function TeachersPage() {
  const { t } = useTranslation(); // Add translation hook
  
  return (
    <main role="main" aria-label="Teachers Page">
      <article className="teachers-page">
        <header className="page-header">
          <h1>👨‍🏫 {t('navigation.teachers', 'For Teachers')}</h1>
          <p>Resources and tools for educators</p>
        </header>
        <section className="content" aria-label="Teacher resources">
          <p>Teacher resources coming soon...</p>
          <p>For inquiries, please email: <a href="mailto:papertips.space@gmail.com">papertips.space@gmail.com</a></p>
        </section>
      </article>
    </main>
  );
}

export default App; 
